import type { StorySettingsType, StoryType } from "@/config";
import { track } from "@/features/analytics";
import { useAuthStoreShallow } from "@/features/authentication";
import {
  isNormalizedNestedStructure,
  isStructuredLayout,
  normalizeToNestedData,
} from "@/shared";
import { DEFAULT_FAVICON } from "@/shared/constants";
import { withDocumentInfoOnFirstSlide } from "@/shared/data/document-info-element";
import {
  TAccount,
  designSystemApi,
  pagesApi,
  useCreatePageMutation,
} from "@/shared/services";
import type { TImportedFrom } from "@/shared/services/pages/types";
import { api } from "@api";
import { useImportStoryBrainMutation } from "@api/story-brain";
import { useAccountStore } from "@features/account-management";
import { useAppDispatch } from "@store";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import { useUpdatePreviewAndMeta } from "./use-update-preview-and-meta";

type TCreateStoryPayload = {
  status: "Draft" | "Live" | "Dev" | "Template" | "Archived" | "Deleted";
  title: string;
  template: StoryType;
  type: "duplicate" | "create" | "template" | "from-template";
  folderId?: string | null;
  forceUseTemplateSlides?: boolean;
  skipToast?: boolean;
  // Keep the template's own designSystemV2 instead of stamping the account's
  // default theme. Used by File-to-Storydoc, which has already resolved the
  // design system (selected theme / PDF-derived palette) onto the template.
  keepTemplateDesignSystem?: boolean;
  importedFrom?: TImportedFrom;
};

export const STORY_CREATION_BLOCKED_MESSAGE =
  "Your role doesn't allow creating stories. Ask an admin to upgrade your seat.";

export const useCreateStory = () => {
  const account = useAccountStore((state) => state.account);
  const canCreateStories = useAuthStoreShallow(
    (state) => state.canCreateStories,
  );
  const orgId = account?._id;
  const navigate = useNavigate();
  const accountDesignSystem = account?.dsId;

  const updatePreviewAndMeta = useUpdatePreviewAndMeta();
  const { mutateAsync: createPage } = useCreatePageMutation();
  const [importStoryBrain] = useImportStoryBrainMutation();
  const dispatch = useAppDispatch();

  const handleCreateStory = async ({
    status = "Draft",
    title,
    template,
    type,
    folderId: targetFolderId,
    forceUseTemplateSlides,
    skipToast,
    keepTemplateDesignSystem,
    importedFrom,
  }: TCreateStoryPayload) => {
    if (!canCreateStories) {
      toast.error(STORY_CREATION_BLOCKED_MESSAGE);
      return;
    }
    try {
      track("click create story");
      // An explicit null means the stories root, which is not the same as not
      // saying — a copy pasted at the root must not fall back to the folder its
      // source lives in.
      let folderId = template.folderId;
      if (targetFolderId !== undefined) folderId = targetFolderId;

      const isStructured = isStructuredLayout(template.layoutVersion);
      let slides: Record<string, any>[] | null = null;

      let isUsingTemplateSlides = false;
      if (
        template.slides?.length &&
        !isNormalizedNestedStructure(template.slides)
      ) {
        isUsingTemplateSlides = true;
        const normalized = normalizeToNestedData(template.slides);
        slides = normalized;
      } else if (
        template.slides?.length &&
        isNormalizedNestedStructure(template.slides)
      ) {
        isUsingTemplateSlides = true;
        slides = template.slides as Record<string, any>[];
      } else if (forceUseTemplateSlides && template.slides?.length) {
        if (!isNormalizedNestedStructure(template.slides)) {
          slides = normalizeToNestedData(template.slides);
        } else slides = template.slides as Record<string, any>[];
      } else {
        slides = !isStructured
          ? await pagesApi.getPageSlides(template._id)
          : null;
      }

      const shouldApplyAccountDefaults = type !== "duplicate";

      let designSystem: Record<string, string> = template.settings
        .designSystemV2 as Record<string, string>;
      // Custom colors from the applied account theme, so a story created from
      // it inherits the same palette (kept consistent with the theme editor).
      let themeCustomColors: string[] | undefined;
      if (
        accountDesignSystem &&
        shouldApplyAccountDefaults &&
        !keepTemplateDesignSystem
      ) {
        const theme = await designSystemApi.getThemeById(accountDesignSystem);
        designSystem = theme.designSystem as Record<string, string>;
        themeCustomColors = theme.customColors;
      }

      let copiedStoryBrainId: string | undefined;
      if (account?.accountStoryBrainId && shouldApplyAccountDefaults) {
        const newStoryBrainId = uuid();
        try {
          await importStoryBrain({
            storyBrainId: account.accountStoryBrainId,
            newStoryBrainId,
          }).unwrap();
          copiedStoryBrainId = newStoryBrainId;
        } catch (e) {
          console.error("Failed to import account knowledge into story:", e);
        }
      }

      const story = {
        status,
        settings: createStorySettings(
          template.settings,
          title,
          account,
          designSystem,
          shouldApplyAccountDefaults,
          themeCustomColors,
        ),
        orgId,
        history: {},
        layoutVersion: window.BUNDLE_VERSION || "5.0.0",
      } as StoryType;

      if (importedFrom) {
        (
          story.settings as StorySettingsType & { importedFrom: TImportedFrom }
        ).importedFrom = importedFrom;
      }

      if (copiedStoryBrainId) {
        story.settings.storyBrainId = copiedStoryBrainId;
      }

      if (folderId && (orgId === template.orgId || targetFolderId)) {
        story.folderId = folderId;
      }

      const contentPointer = template.settings.contentPointer as string;

      const [content, titleCount] = await Promise.all([
        !isStructured || isUsingTemplateSlides || forceUseTemplateSlides
          ? { slides }
          : (await fetch(contentPointer)).json(),
        pagesApi
          .getStoryTitleCount({
            title: prepareTitleToCheck(title),
            id: template._id,
          })
          .catch(() => ({ count: 0 })),
      ]);

      // Every newly created story carries the Document info pills on its
      // first slide — template, AI-deck, and import paths included, which
      // never pass through DEFAULT_STORY's seeded blank slide. Duplicates
      // and template conversions copy the source verbatim.
      if (
        (type === "create" || type === "from-template") &&
        content?.slides?.length
      ) {
        content.slides = withDocumentInfoOnFirstSlide(content.slides);
      }

      if (titleCount.count > 0 && title.includes("Copy of")) {
        title = `${prepareTitleToCheck(title)} (${titleCount.count + 1})`;
        story.settings.title = title;
      }

      // `settings` is spread from the TEMPLATE (or the duplicated story), so
      // `og` — and with it the reader-facing "Public title" — arrives as the
      // source's, while `settings.title` has already been overwritten with the
      // name the user just typed. Every rename path keeps the two in sync
      // (`header/components/title.tsx`, `story-details/hooks/use-story-details`,
      // `change-story-name-dialog/components/footer`), so creation was the only
      // place the pair could be born divergent — leaving surfaces that read
      // `og.siteName` (the Document header preview, the published/preview
      // `<title>`, and the engagement bar, which mirrors `document.title`)
      // showing the template's title instead of the story's. Seed it here,
      // after the "Copy of … (n)" adjustment above, so it is written once from
      // the final name on every path.
      story.settings.og = {
        ...story.settings.og,
        siteName: story.settings.title,
      };

      const page = await createPage(story);
      const updated = await updatePreviewAndMeta({
        story: {
          ...page,
          slides: content.slides,
          settings: {
            ...page.settings,
          },
        },
        skipUpdate: false,
      });

      if (type === "from-template" && updated?._id) {
        navigate(`/pages/editor/${updated._id}?autosave=true`);
        return;
      }

      if (type !== "template") {
        dispatch(
          api.util.invalidateTags([
            { type: "Stories", id: "SEARCH" },
            { type: "Stories", id: "RECENT" },
          ]),
        );
      }

      if (!skipToast) {
        const messages = {
          duplicate: `"${template.settings.title}" duplicated`,
          template: `"${title}" converted to template`,
          create: `"${title}" created`,
        };
        const message =
          messages[type as keyof typeof messages] || messages.create;
        toast.success(message);
      }
      // `creation method` / `document type` / `entry point` / `signup flow version`
      // ride along as super properties registered earlier in the funnel.
      track("success create story", {
        "creation type": type,
        "template id": template._id,
        "story id": updated?._id,
      });
      return updated;
    } catch (e) {
      console.error(e);
      track("failed create story", { error: e });
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      if (status === 403) {
        toast.error(STORY_CREATION_BLOCKED_MESSAGE);
        return;
      }
      toast.error("Failed to create story");
      throw e;
    }
  };

  return handleCreateStory;
};

const createStorySettings = (
  templateSettings: any,
  title: string,
  account: TAccount | null,
  designSystem: Record<string, string>,
  shouldApplyAccountDefaults: boolean,
  customColors?: string[],
) => {
  const settings = {
    ...templateSettings,
    title,
    publicContentPointer: null,
    canonical: uuid(),
  } as StorySettingsType;
  delete (settings as StorySettingsType & { importedFrom?: unknown })
    .importedFrom;

  if (!shouldApplyAccountDefaults) {
    return settings;
  }

  settings.designSystemV2 = designSystem;
  // Inherit the applied theme's custom-color palette. Only override when the
  // theme actually carries colors, so we don't wipe a template's own palette.
  if (customColors) settings.customColors = customColors;
  (
    settings as StorySettingsType & { storydocBranding: boolean }
  ).storydocBranding = !account?.hidePoweredByStorydoc;
  settings.favicon = account?.favicon || DEFAULT_FAVICON;

  if (account?.analyticsDisable) {
    settings.analyticsDisable = account.analyticsDisable;
  }

  if (account?.defaultLanguage) {
    settings.language = account.defaultLanguage;
  }

  if (account?.defaultEngagementBar) {
    settings.engagementBar = {
      ...account.defaultEngagementBar,
      presetId: account.defaultEngagementBar.presetId ?? "",
    };
    settings.engagementBarEnabled = account.defaultEngagementBar.active;
  }

  if (account?.defaultWatermark) {
    (settings as StorySettingsType & { watermark: unknown }).watermark =
      account.defaultWatermark;
  }

  return settings;
};

const prepareTitleToCheck = (title: string) => {
  const countIndex = title.lastIndexOf("(");
  if (countIndex !== -1) title = title.slice(0, countIndex).trim();
  return `Copy of ${title.replace(/Copy of /g, "")}`;
};
